// @flow

import React, {Component} from 'react'
import '../App.css';
import Utils from "../Utils";
import Slider from '@material-ui/lab/Slider';
import {RNG} from "../RNG";
import Interval from "./Interval";
import {GridNode} from "./Models";
import Colors from "../Colors";
import Waypoint from "react-waypoint";
import WidgetButton from "./WidgetButton";


const MEDIA_OUTLET_RADIUS = 15;
const MEDIA_OUTLET_DENSITY = 6;
// const MEDIA_OUTLET_REFRACTORY_PERIOD = 100;

type Props = {
  randomSeed?: number,

  // Network
  addCities?: boolean,
  addLinkedNodes?: boolean,
  addMediaOutlets?: boolean,
  degree?: number,
  gridCols?: number,
  gridRows?: number,

  // Simulation parameters
  immunityFraction?: number,
  maxActiveNodes?: number,
  maxIterations?: number,
  reinfectionsAllowed?: boolean,
  transmissionProbability?: number,
  vacuumProbability?: number,

  // Science stuff
  careeristPreferenceForTruth?: number,
  inventorDistance?: number,
  modelKnowledgeGrowth?: boolean,
  modelScience?: boolean,
  realScientistFraction?: number,
  scientistPreferenceForTruth?: number,

  // Rendering parameters
  drawNodeOutlines?: boolean,
  nodeSize?: number,
  speed?: number,

  // Controls
  highlight?: string,
  immunitySliderName?: string,
  showAliveFraction?: boolean,
  showAllControls?: boolean,
  showDegreeSlider?: boolean,
  showImmunityFractionSlider?: boolean,
  showPlaybackControls?: boolean,
  showProTip?: boolean,
  showSimulationButtons?: boolean,
  showSpeedControls?: boolean,
  showTransmissionProbabilitySlider?: boolean,
  showVacuumProbabilitySlider?: boolean,
}

type State = {
  numActiveNodes: number,
  playing: boolean,
  visible: boolean,

  // Network
  degree: number,

  // Simulation
  immunityFraction: number,
  longDistaceNetworkActive: boolean,
  maxActiveNodes: number,
  maxIterations: number,
  networkEffectStrength: number,
  transmissionProbability: number,
  vacuumProbability: number,

  // Rendering
  drawNodeOutlines: boolean,
  gridWidth: number,
  nodeSize: number,
  speed: number,  // between 0 and 1

  // Science stuff
  careeristPreferenceForTruth: number,
  realScientistFraction: number,
  scientistPreferenceForTruth: number,
}

export class NodeLegend extends Component<{type: string}> {
  render() {
    let color = Grid.SUSCEPTIBLE_COLOR;
    if (this.props.type === 'infected') {
      color = Grid.INFECTED_COLOR;
    } else if (this.props.type === 'removed') {
      color = Grid.REMOVED_COLOR;
    }
    return <div style={{marginBottom: "-2px", border: "1px black solid", width: "1rem", height: "1rem", backgroundColor: color, display: "inline-block"}}/>;
  }
}

export default class Grid extends Component<Props, State> {
  static NEIGHBOR_CLASSES = [
      [[0, 0]],
      [[-1, 0], [1, 0], [0, -1], [0, 1]],
      [[-1, -1], [-1, 1], [1, -1], [1, 1]],
      [[-2, -2], [-2, -1], [-2, 0], [-2, 1], [-2, 2], [-1, 2], [0, 2], [1, 2], [2, 2], [2, 1], [2, 0], [2, -1], [2, -2], [1, -2], [0, -2], [-1, -2]],
  ];

  static INFECTED_COLOR = '#48F';
  static SUSCEPTIBLE_COLOR = '#EEE';
  static REMOVED_COLOR = '#C5C5C5';

  // Set default props
  static defaultProps = {
    randomSeed: -1,

    // Network
    addCities: false,
    addLinkedNodes: false,
    addMediaOutlets: false,
    degree: 4,
    gridCols: 1,
    gridRows: 1,

    // Simulation parameters
    immunityFraction: 0,
    maxActiveNodes: -1,
    maxIterations: -1,
    nug: 20,
    reinfectionsAllowed: true,
    transmissionProbability: 0.3,
    vacuumProbability: 0,

    // Science stuff
    careeristPreferenceForTruth: 0.5,
    inventorDistance: 4,
    modelKnowledgeGrowth: false,
    modelScience: false,
    realScientistFraction: 0,
    scientistPreferenceForTruth: 1.0,

    // Rendering parameters
    drawNodeOutlines: true,
    speed: 0.5,

    // Controls
    immunitySliderName: "Immunity",
    showAliveFraction: false,
    showAllControls: false,
    showDegreeSlider: false,
    showImmunityFractionSlider: false,
    showPlaybackControls: false,
    showProTip: false,
    showSimulationButtons: false,
    showSpeedControls: false,
    showTransmissionProbabilitySlider: false,
    showVacuumProbabilitySlider: false,
  };

  grid: GridNode[][];
  rng: RNG;

  // Weird rendering parameters; don't want React trying to auto-manage these
  gridWidth: number;
  nodeSize: number;

  canvasRef: any;

  constructor(props: Props) {
    super(props);

    this.canvasRef = React.createRef();

    this.previousSimulationParams = ['foo'];

    this.previousDrawingParams = [];

    this.onTick = this.onTick.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);

    this.initializeFromProps(this.props, true);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
  }

  updateWindowDimensions() {
    let idealWidth = this.props.gridCols * this.props.nodeSize;
    if (this.props.nodeSize >= 5) {
      idealWidth += this.props.gridCols;
    }

    let gridWidth = Math.min(idealWidth, document.documentElement.clientWidth - 40);
    let nodeSize = Math.floor(gridWidth / this.props.gridCols);

    gridWidth = nodeSize * this.props.gridCols;

    if (this.gridWidth !== gridWidth || this.nodeSize !== nodeSize) {
      this.gridWidth = gridWidth;
      this.nodeSize = nodeSize;
      this.redraw(true);
    }
  }

  componentWillReceiveProps(nextProps: Props, nextContext: any): void {
    // this.initializeFromProps(nextProps, false);
  }

  initializeFromProps(props: Props, fromConstructor: boolean) {
    this.gridWidth = props.gridCols * props.nodeSize;
    this.nodeSize = props.nodeSize;

    let randomSeed = props.randomSeed;
    if (randomSeed === -1) {
      randomSeed = Math.floor(Math.random() * 3000000);
      console.log('ack');
    }
    this.rng = new RNG(randomSeed);

    let state = {
      numActiveNodes: 0,
      playing: false,
      visible: false,

      // Network
      degree: props.degree,

      // Simulation
      immunityFraction: props.immunityFraction,
      longDistaceNetworkActive: props.addLinkedNodes,
      maxActiveNodes: props.maxActiveNodes,
      maxIterations: props.maxIterations,
      networkEffectStrength: 0,
      transmissionProbability: props.transmissionProbability,
      vacuumProbability: props.vacuumProbability,

      // Science stuff
      careeristPreferenceForTruth: props.careeristPreferenceForTruth,
      realScientistFraction: props.realScientistFraction,
      scientistPreferenceForTruth: props.scientistPreferenceForTruth,

      // Rendering
      drawNodeOutlines: props.drawNodeOutlines,
      speed: props.speed,
    };
    if (fromConstructor) {
      this.state = state;
    } else {
      this.setState(state);
    }

    // if (this.interval !== null && this.interval !== undefined) {
    //   clearInterval(this.interval);
    // }
    // this.interval = setInterval(this.onTick, 10);
  }

  componentDidMount() {
    this.canvas = this.canvasRef.current;

    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);

    // this.regenerate();
    this.redraw(true);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  onTick() {
    if (this.state.playing && this.state.visible) {
      this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      // this.simulateStep();
      this.redraw(true);
    }
  }

  onEnter() {
    this.setState({
      visible: true,
    });
    this.redraw(true);
  }

  onLeave() {
    this.setState({
      visible: false,
    });
  }

  static shuffleInPlace(arr, rng: RNG) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  regenerate() {
    this.generate();
    this.forceUpdate();
  }

  generate(force: boolean) {
    // actually regenerate iff any of the simulation parameters have changed
    let currentSimulationParams = [
        // this.state.immunityFraction,
    ];
    if (!force && Utils.arraysEqual(this.previousSimulationParams, currentSimulationParams)) {
      // console.log('rejecting generate');
      return;
    }

    this.previousSimulationParams = currentSimulationParams;

    console.log('Generating new network');

    let nRows = this.props.gridRows;
    let nCols = this.props.gridCols;

    // Initialize grid
    this.grid = [];
    for (let r = 0; r < nRows; r++) {
      let row = [];
      for (let c = 0; c < nCols; c++) {
        let node = new GridNode(this.rng);
        node.immune = this.rng.random() < this.state.immunityFraction;

        row.push(node);
      }
      this.grid.push(row);
    }

    // Add linked nodes
    let linkedNodes: Set<GridNode> = new Set();
    if (this.props.addLinkedNodes) {
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          let r = Math.floor((nRows / 6) * (2 * i + 1));
          let c = Math.floor((nCols / 6) * (2 * j + 1));
          let node = this.grid[r][c];
          node.linked = true;
          node.setSusceptible();  // make sure it's not removed
          linkedNodes.add(node);
        }
      }
    }

    // Add cumulative knowledge nodes
    let n1row, n1col;
    if (this.props.modelKnowledgeGrowth) {
      let spread = this.props.inventorDistance;

      n1row = Math.floor(1 * nRows / spread);
      n1col = Math.floor(1 * nRows / spread);

      let n1 = this.grid[Math.floor(1 * nRows / spread)][Math.floor(1 * nRows / spread)];
      let n2 = this.grid[Math.floor(1 * nRows / spread)][Math.floor((spread-1) * nRows / spread)];
      let n3 = this.grid[Math.floor((spread-1) * nRows / spread)][Math.floor((spread-1) * nRows / spread)];
      let n4 = this.grid[Math.floor((spread-1) * nRows / spread)][Math.floor(1 * nRows / spread)];

      n1.setKnowledgeLevel(1);
      n2.setKnowledgeLevel(2);
      n3.setKnowledgeLevel(3);
      n4.setKnowledgeLevel(4);
    } else {
      n1row = null;
      n1col = null;
    }

    // Fix number of removed cells
    if (this.props.reinfectionsAllowed) {
      this.updateRemovedCells(0, linkedNodes);
    }

    // Model scientists
    if (this.props.modelScience) {
      Grid.updateRealScientists(this.grid, this.state.realScientistFraction, 0, this.rng);
    }

    // Add cities
    if (this.props.addCities) {
      let cityCenters = [];
      cityCenters.push([Math.floor(3/4 * nRows), Math.floor(1/4 * nCols)]);
      cityCenters.push([Math.floor(1/4 * nRows), Math.floor(3/4 * nCols)]);
      for (let r = 0; r < nRows; r++) {
        for (let c = 0; c < nCols; c++) {
          for (let center of cityCenters) {
            let cr = center[0];
            let cc = center[1];
            let distance = Math.sqrt(Math.pow(cr - r, 2) + Math.pow(cc - c, 2));
            if (distance <= 16) {
              this.grid[r][c].specialDegree = 8 - Math.floor(distance/4);
            }
          }
        }
      }
    }

    // Add media outlets
    if (this.props.addMediaOutlets) {
      for (let i = 0; i < MEDIA_OUTLET_DENSITY; i++) {
        for (let j = 0; j < MEDIA_OUTLET_DENSITY; j++) {
          let r = Math.floor((nRows / (2 * MEDIA_OUTLET_DENSITY)) * (2 * i + 1));
          let c = Math.floor((nCols / (2 * MEDIA_OUTLET_DENSITY)) * (2 * j + 1));
          this.grid[r][c].setMediaOutlet();
        }
      }
    }

    // Initialize nug
    let centerR = Math.floor((nRows - 1) / 2);
    let centerC = Math.floor((nCols - 1) / 2);
    if (this.props.modelScience) {
      for (let dr = -4; dr <= 4; dr++) {
        for (let dc = -4; dc <= 4; dc++) {
          let node = this.grid[centerR+dr][centerC+dc];
          node.setInfected(node.isRealScientist() ? 'REAL' : 'FAKE', 1);
        }
      }
    } else if (this.props.modelKnowledgeGrowth) {
      if (n1row !== null && n1col !== null) {  // they better not be null....
        // this.grid[n1row+0][n1col+0].setInfected('1', 1);
        if (this.props.nug === 20) {
          for (let dr = -2; dr <= 2; dr++) {
            for (let dc = -2; dc <= 2; dc++) {
              if (Math.abs(dr) === 2 && Math.abs(dc) === 2) {
                continue;
              }

              this.grid[n1row+dr][n1col+dc].setInfected('1', 1);
            }
          }
        }
      }
    } else {
      if (this.props.nug === 1) {
        this.grid[centerR][centerC].setInfected('REAL', 1);
      } else if (this.props.nug === 20) {
        for (let dr = -2; dr <= 2; dr++) {
          for (let dc = -2; dc <= 2; dc++) {
            if (Math.abs(dr) === 2 && Math.abs(dc) === 2) {
              continue;
            }

            this.grid[centerR+dr][centerC+dc].setInfected('REAL', 1);
          }
        }
      }
    }

    this.redraw(true);
    this.setState({
      numActiveNodes: this.props.nug,
    })
  }

  simulateStep() {
    let nRows = this.props.gridRows;
    let nCols = this.props.gridCols;

    let actualRemovedCells = 0;
    let actualRealScientists = 0;
    let linkedNodes: Set<GridNode> = new Set();

    // Save previous step states, and reset current infected nodes to susceptible
    for (let r = 0; r < nRows; r++) {
      for (let c = 0; c < nCols; c++) {
        let node = this.grid[r][c];
        node.savePreviousState();
        node.resetInfectionWeight();

        if (node.isInfected()) {
          if (this.props.reinfectionsAllowed) {
            node.setSusceptible();
          } else {
            node.setRemoved();
          }
        }

        if (node.isRemoved()) {
          actualRemovedCells++;
        }
        if (node.isRealScientist()) {
          actualRealScientists++;
        }

        if (this.state.longDistaceNetworkActive && node.linked) {
          linkedNodes.add(node);
        }
      }
    }

    // Fix number of removed cells
    if (this.props.reinfectionsAllowed) {
      this.updateRemovedCells(actualRemovedCells, linkedNodes);
    }

    // Fix number of scientists
    if (this.props.modelScience) {
      Grid.updateRealScientists(this.grid, this.state.realScientistFraction, actualRealScientists, this.rng);
    }

    // Infect
    for (let r = 0; r < nRows; r++) {
      for (let c = 0; c < nCols; c++) {
        let node = this.grid[r][c];
        if (node.wasInfected()) {
          this.tryInfectingNeighbors(node, r, c, linkedNodes);
        }
      }
    }

    // Apply vacuum probability
    if (this.state.vacuumProbability > 0) {
      for (let r = 0; r < nRows; r++) {
        for (let c = 0; c < nCols; c++) {
          let node = this.grid[r][c];
          if (node.isSusceptible()) {
            if (this.rng.random() < this.state.vacuumProbability) {
              node.setInfected('REAL', 1);
            }
          }
        }
      }
    }

    // See what we have left
    let actualInfectedNodes = 0;
    for (let r = 0; r < nRows; r++) {
      for (let c = 0; c < nCols; c++) {
        let node = this.grid[r][c];
        if (node.isInfected()) {
          actualInfectedNodes++;
        }
      }
    }

    // Update the number of active nodes, and the playing bit if necessary
    this.setState({
      numActiveNodes: actualInfectedNodes,
      playing: this.state.playing && (actualInfectedNodes !== 0 || this.state.vacuumProbability > 0),
    });

    // Prune if necessary
    let maxInfectedNodes = this.state.maxActiveNodes;
    if (maxInfectedNodes !== -1 && actualInfectedNodes > maxInfectedNodes) {
      // Go through and deactive randomly
      let nodesToKill = actualInfectedNodes - maxInfectedNodes;
      let infectedNodesRemaining = actualInfectedNodes;
      for (let r = 0; r < nRows; r++) {
        for (let c = 0; c < nCols; c++) {
          let node = this.grid[r][c];
          if (node.isInfected()) {
            let deactivate = this.rng.random() < nodesToKill / infectedNodesRemaining;
            if (deactivate) {
              node.setSusceptible();
              nodesToKill--;
            }

            infectedNodesRemaining--;
          }
        }
      }
    }

    this.redraw(true);
  }

  updateRemovedCells(actualRemovedCells: number, linkedNodes: Set<Node>) {
    let nRows = this.grid.length;
    let nCols = this.grid[0].length;

    let desiredRemovedCells = Math.floor(nRows * nCols * this.state.immunityFraction);
    let removedCellDelta = desiredRemovedCells - actualRemovedCells;
    if (removedCellDelta > 0) {
      let nonRemovedNodesLeft = nRows * nCols - actualRemovedCells - linkedNodes.size;
      for (let r = 0; r < nRows; r++) {
        for (let c = 0; c < nCols; c++) {
          let node = this.grid[r][c];
          if (!node.isRemoved() && node.isAllowedToBeRemoved()) {
            let toggle = this.rng.random() < Math.abs(removedCellDelta) / nonRemovedNodesLeft;
            if (toggle) {
              node.setRemoved();
              removedCellDelta--;
            }
            nonRemovedNodesLeft--;
          }
        }
      }
    } else if (removedCellDelta < 0) {
      let immuneCellsLeft = actualRemovedCells;
      for (let r = 0; r < nRows; r++) {
        for (let c = 0; c < nCols; c++) {
          let node = this.grid[r][c];
          if (node.isRemoved()) {
            let toggle = this.rng.random() < Math.abs(removedCellDelta) / immuneCellsLeft;
            if (toggle) {
              node.setSusceptible();
              removedCellDelta++;
            }
            immuneCellsLeft--;
          }
        }
      }
    }
  }

  static updateRealScientists(grid: Node[][], realScientistFraction: number, actualRealScientists: number, rng: RNG) {
    let nRows = grid.length;
    let nCols = grid[0].length;

    let desiredRealScientists = Math.floor(realScientistFraction * nRows * nCols);
    if (desiredRealScientists !== actualRealScientists) {
      let numNodesToToggle = Math.abs(desiredRealScientists - actualRealScientists);

      let sign = 1;
      let numTogglableNodesRemaining = null;
      if (desiredRealScientists > actualRealScientists) {
        sign = 1;
        numTogglableNodesRemaining = nRows * nCols - actualRealScientists;
      } else {
        sign = -1;
        numTogglableNodesRemaining = actualRealScientists;
      }

      for (let r = 0; r < nRows; r++) {
        for (let c = 0; c < nCols; c++) {
          let node = grid[r][c];
          let isTogglable = sign === 1 ? !node.isRealScientist() : node.isRealScientist();
          if (isTogglable) {
            let toggle = rng.random() < numNodesToToggle / numTogglableNodesRemaining;
            if (toggle) {
              node.setRealScientist(!node.isRealScientist());
              numNodesToToggle--;
            }
            numTogglableNodesRemaining--;
          }
        }
      }
    }
  }

  tryInfectingNeighbors(node: GridNode, r: number, c: number, linkedNodes: Set<GridNode>) {
    let neighbors = this.getNeighbors(node, r, c, linkedNodes);

    let transProb = this.state.transmissionProbability;

    if (this.state.networkEffectStrength > 0) {
      let totalNeighbors = neighbors.length;
      let activeNeighbors = 0;
      for (let neighbor of neighbors) {
        if (neighbor.wasInfected()) {
          activeNeighbors++;
        }
      }
      let newTransProb = transProb + (transProb / totalNeighbors) * (activeNeighbors - totalNeighbors / 2) * this.state.networkEffectStrength;
      // console.log(r, c, transProb, newTransProb);
      transProb = newTransProb;
    }

    for (let neighbor of neighbors) {
      node.tryToInfect(neighbor, transProb,
          this.props.modelScience, this.state.scientistPreferenceForTruth, this.state.careeristPreferenceForTruth)
    }
  }

  getNeighbors(node: GridNode, r: number, c: number, linkedNodes: Set<GridNode>): GridNode[] {
    let nRows = this.grid.length;
    let nCols = this.grid[0].length;

    let neighbors = [];

    if (node.isMediaOutlet()) {
      for (let dr = -MEDIA_OUTLET_RADIUS; dr <= MEDIA_OUTLET_RADIUS; dr++) {
        for (let dc = -MEDIA_OUTLET_RADIUS; dc <= MEDIA_OUTLET_RADIUS; dc++) {
          let cr = r + dr;
          let cc = c + dc;
          if (cr >= 0 && cr < nRows && cc >= 0 && cc < nCols) {
            let distance = Math.sqrt(Math.pow(cr - r, 2) + Math.pow(cc - c, 2));
            if (distance <= MEDIA_OUTLET_RADIUS) {
              neighbors.push(this.grid[cr][cc]);
            }
          }
        }
      }
    } else {
      let degree = this.state.degree;
      if (this.props.addCities && node.specialDegree !== null) {
        degree = node.specialDegree;
      }

      let attempts = -1;  // first one's free!
      let nclassIndex = 0;
      let nclass = Grid.NEIGHBOR_CLASSES[nclassIndex].slice(0);
      while (attempts < degree) {
        attempts++;

        if (nclass.length === 0) {
          // grab next neighbor class
          nclassIndex++;
          nclass = Grid.NEIGHBOR_CLASSES[nclassIndex].slice(0);

          // shuffle new neighbor class
          Grid.shuffleInPlace(nclass, this.rng);
        }

        let neighbDeltas = nclass.pop();

        let cr = r + neighbDeltas[0];
        let cc = c + neighbDeltas[1];
        if (cr >= 0 && cr < nRows && cc >= 0 && cc < nCols) {
          neighbors.push(this.grid[cr][cc]);
        }
      }
    }

    if (node.linked && this.state.longDistaceNetworkActive) {
      for (let neighbor of linkedNodes) {
        neighbors.push(neighbor);
      }
    }

    return neighbors
  }

  togglePlayback() {
    if (this.state.numActiveNodes === 0 && this.state.vacuumProbability === 0) {
      // If network is dead, play button acts as reset + play button.
      this.generate(true);
    }
    this.setState({
      playing: !this.state.playing,
    });
  }

  redraw(force: boolean) {
    if (this.canvas === null || this.canvas === undefined) {
      console.log('no canvas');
      return;
    }

    // actually redraw iff any of the drawing parameters have changed
    let currentDrawingParams = [
        this.network,
        this.state.drawNodeOutlines,
        this.state.longDistaceNetworkActive,
    ];
    if (!force && Utils.arraysEqual(this.previousDrawingParams, currentDrawingParams)) {
      // console.log('no draw');
      return;
    }
    this.previousDrawingParams = currentDrawingParams;


    // console.log('redrawing...');

    let context = this.canvas.getContext('2d');
    context.fillStyle = '#FFF';
    context.fillRect(0, 0, this.gridWidth, this.gridWidth);

    for (let r = 0; r < this.grid.length; r++) {
      for (let c = 0; c < this.grid[r].length; c++) {
        let node = this.grid[r][c];
        this.drawCell(r, c, node, context);
      }
    }
  }

  drawCell(r: number, c: number, node: GridNode, context) {
    let w = this.nodeSize;
    let y = r * w;
    let x = c * w;

    if (node.isInfected()) {
      if (node.getInfectionType() === 'REAL') {
        context.fillStyle = Grid.INFECTED_COLOR;
      } else if (node.getInfectionType() === '1' || node.getInfectionType() === '2' ||
                 node.getInfectionType() === '3' || node.getInfectionType() === '4') {
        context.fillStyle = node.getInfectionTypeColor();
      } else {
        context.fillStyle = '#F66';
      }
    } else if (node.isRemoved()) {
      context.fillStyle = Grid.REMOVED_COLOR;
    } else {
      // Node is susceptible
      context.fillStyle = Grid.SUSCEPTIBLE_COLOR;

      if (this.props.modelScience) {
        if (node.isRealScientist()) {
          context.fillStyle = '#DDF';
        } else {
          context.fillStyle = '#FDD';
        }
      } else if (node.specialDegree !== null) {
        // should be somewhere between 4 and 8
        Utils.assert(node.specialDegree >= 4 && node.specialDegree <= 8, "node.specialDegree should be between 4 and 8; was: " + node.specialDegree);
        let intensity = (node.specialDegree - 4) / 4.0;
        context.fillStyle = Colors.hex(Colors.blend(Colors.makeHex(Grid.SUSCEPTIBLE_COLOR), Colors.makeHex('#BBB'), intensity))
      }

      if (this.props.modelKnowledgeGrowth) {
        context.fillStyle = node.getKnowledgeLevelColor();
      }
    }

    let gap = 1;
    if (this.nodeSize < 5 || this.nodeSize < this.props.nodeSize) {
      gap = 0;
    }

    // context.fillRect(x, y, w, w);
    context.fillRect(x, y, w - gap, w - gap);
    // context.beginPath();
    // context.arc(x+w/2, y+w/2, w/2-1, 0, 2 * Math.PI);
    // context.fill();

    if ((node.linked && this.state.longDistaceNetworkActive) ||
        node.isMediaOutlet()) {
      // context.beginPath();
      context.lineWidth = 1;
      context.strokeStyle = '#484';
      context.strokeRect(x+0.5, y+0.5, w - gap, w - gap);
      // context.stroke();
    } else if (this.props.modelKnowledgeGrowth && node.getKnowledgeLevel() !== null) {
      context.lineWidth = 2;
      context.strokeStyle = node.getKnowledgeLevelColor();
      context.strokeRect(x+0.5, y+0.5, w - gap, w - gap);
    }
  }

  static renderPercentage(fraction: number) {
    let percent = Math.round(fraction * 100);
    return <span><strong>{percent}</strong>%</span>;
  }

  // noinspection JSMethodCanBeStatic
  renderSlider(name: string, value: number, onChange: Function, min: number, max: number, step: number,
               renderPercentage: boolean, highlighted: boolean) {
    let valueStr;
    if (renderPercentage === 0) {
      valueStr = "";
    } else if (renderPercentage) {
      valueStr = <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{Grid.renderPercentage(value)}</span>;
    } else {
      valueStr = <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<strong>{Math.round(value*100)/100}</strong></span>;
    }

    let highlightedClass = "";
    // if (highlighted) {
    //   highlightedClass = " highlighted"
    // }

    return (
      <div className="slider-container">
        <div className={"slider-name" + highlightedClass}>{name}{valueStr}</div>
        <div className="slider-slider">
          <Slider classes={{
                    container: 'slider-slider-container',
                    thumbIconWrapper: "",
                  }}
                  // thumb={
                  //   <img
                  //     alt="slider thumb icon"
                  //     src="/static/images/misc/circle.png"
                  //   />
                  // }
                  min={min}
                  max={max}
                  step={step}
                  value={value}
                  onChange={onChange}/>
        </div>
        <div className="slider-minus">
          <WidgetButton size="small" onClick={() => onChange(null, Math.max(value - step, min))}><span className="plus-minus-button">–</span></WidgetButton>
        </div>
        <div className="slider-plus">
          <WidgetButton size="small" onClick={() => onChange(null, Math.min(value + step, max))}><span className="plus-minus-button">+</span></WidgetButton>
        </div>
      </div>
    );
  }

  render() {
    this.generate();
    this.redraw();

    let showAll = this.props.showAllControls;

    let transmissionProbabilitySlider = null;
    if (showAll || this.props.showTransmissionProbabilitySlider) {
      transmissionProbabilitySlider =
          this.renderSlider("Transmission rate", this.state.transmissionProbability,
              (e, value) => { this.setState({transmissionProbability: value}); },
              0, 1, 0.01, true, this.props.highlight === "transmissionRate");
    }

    let vacuumProbSlider = null;
    if (showAll || this.props.showVacuumProbabilitySlider) {
      let maxProb = 1 / (this.props.gridRows * this.props.gridCols) * 1 / 5;
      let probStep = maxProb/100;

      vacuumProbSlider =
          this.renderSlider("Spontaneous activation rate", this.state.vacuumProbability,
              (e, value) => { this.setState({vacuumProbability: value}) },
              0, maxProb, probStep, 0, this.props.highlight === "vacuum")
    }

    let immunityFractionSlider = null;
    if (showAll || this.props.showImmunityFractionSlider) {
      let sliderName = this.props.immunitySliderName || "Immunity";

      immunityFractionSlider =
          this.renderSlider(sliderName, this.state.immunityFraction,
              (e, value) => { this.setState({immunityFraction: value}); },
              0, 1, 0.01, true, this.props.highlight === "immunity");
    }

    let degreeSlider = null;
    if (showAll || this.props.showDegreeSlider) {
      degreeSlider =
          this.renderSlider("Degree", this.state.degree,
              (e, value) => { this.setState({degree: value}); },
              1, 8, 1, false, this.props.highlight === "degree");
    }

    let maxActiveNodesSlider = null;
    let networkEffectSlider = null;
    if (showAll || this.props.maxActiveNodes > 0) {
      maxActiveNodesSlider =
          this.renderSlider("Max active nodes", this.state.maxActiveNodes,
              (e, value) => { this.setState({maxActiveNodes: value}); },
              1, this.props.gridRows * this.props.gridCols / 10, 1, false, false);
      networkEffectSlider =
          this.renderSlider("Network effect", this.state.networkEffectStrength,
              (e, value) => { this.setState({networkEffectStrength: value}); },
              0, 3, 1, false, false);
    }

    // let speedSlider = null;
    // let speedMinusButton = null;
    // let speedPlusButton = null;
    // if (showAll || this.props.showSpeedControls) {
    //   speedMinusButton = <WidgetButton onClick={() => { this.setState({speed: Math.max(0, this.state.speed - 0.20)}) }}>🚶</WidgetButton>;
    //   speedPlusButton = <WidgetButton onClick={() => { this.setState({speed: Math.min(1, this.state.speed + 0.20)}) }}>🏃</WidgetButton>;
    //   speedSlider =
    //       this.renderSlider("Speed", this.state.speed,
    //           (e, value) => { this.setState({speed: value}); },
    //           0, 1, 0.01, 0, false);
    // }

    let playbackControls = null;
    if (showAll || this.props.showPlaybackControls) {
      let newNetworkButton = <WidgetButton onClick={() => {this.setState({playing: false}); this.generate(true); this.forceUpdate();} } >Reset</WidgetButton>;
      let text = <span style={{fontSize: '10pt'}}>▷</span>;
      if (this.state.playing) {
        text = <span><b>||</b></span>;
      }
      let togglePlaybackButton = <WidgetButton highlighted={!this.state.playing} onClick={() => {this.togglePlayback(); } } >{text}</WidgetButton>;
      let stepButton = <WidgetButton onClick={() => {this.simulateStep(); this.setState({playing: false}); } } >Step</WidgetButton>;

      playbackControls =
        <div className='playback-controls-container'>
          {newNetworkButton}
          {togglePlaybackButton}
          {stepButton}

          {/*{speedMinusButton}*/}
          {/*{speedPlusButton}*/}
        </div>
    }

    let toggleLongDistanceNetwork = null;
    if (this.props.addLinkedNodes) {
      let text = 'Long distance: disabled';
      if (this.state.longDistaceNetworkActive) {
        text = 'Long distance: enabled';
      }
      toggleLongDistanceNetwork = <div><span onClick={() => {this.setState({longDistaceNetworkActive: !this.state.longDistaceNetworkActive}); } } >{text}</span></div>;
    }

    let percentAliveSlider = null;
    if (this.props.showAliveFraction || showAll) {
      let fractionAlive = this.state.numActiveNodes / (this.props.gridRows * this.props.gridCols);
      percentAliveSlider = <div>
        <Slider style={{height: this.gridWidth, marginLeft: '0.5rem'}}
                classes={{
                  track: { color: 'pink', width: 50, height: 100 },
                  thumb: { display: 'none' },
                }}
                min={0}
                max={1}
                value={fractionAlive}
                thumb={<span/>}
                vertical
                />
      </div>
    }

    let realScientistFractionSlider = null;
    let scientistPreferenceForTruthSlider = null;
    let careeristPreferenceForTruthSlider = null;
    if (this.props.modelScience) {
      realScientistFractionSlider =
          this.renderSlider("Real scientists", this.state.realScientistFraction,
              (e, value) => { this.setState({realScientistFraction: value}); },
              0, 1, 0.01, true, false);
      scientistPreferenceForTruthSlider =
          this.renderSlider("Scientist truth pref", this.state.scientistPreferenceForTruth,
              (e, value) => { this.setState({scientistPreferenceForTruth: value}); },
              0, 1, 0.01, true, false);
      careeristPreferenceForTruthSlider =
          this.renderSlider("Careerist truth pref", this.state.careeristPreferenceForTruth,
              (e, value) => { this.setState({careeristPreferenceForTruth: value}); },
              0, 1, 0.01, true, false);
    }

    let protip = null;
    if (this.props.showProTip) {
      protip = (
          <div style={{color: '#666', fontSize: '12pt', marginTop: '1em'}}>👆 Pro-tip: You can adjust any slider while the simulation is running.</div>
      );
    }

    let intervalMillis = 1000 * (1-Math.pow(this.state.speed, 1/5));

    let highlightedSlider = null;
    if (this.props.highlight === "transmissionRate") {
      highlightedSlider = transmissionProbabilitySlider;
      transmissionProbabilitySlider = null;
    } else if (this.props.highlight === "vacuum") {
      highlightedSlider = vacuumProbSlider;
      vacuumProbSlider = null;
    } else if (this.props.highlight === "degree") {
      highlightedSlider = degreeSlider;
      degreeSlider = null;
    } else if (this.props.highlight === "immunity") {
      highlightedSlider = immunityFractionSlider;
      immunityFractionSlider = null;
    }

    return (
        <div className="widget-container" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <Waypoint onEnter={this.onEnter} onLeave={this.onLeave} scrollableAncestor={window}>
              <canvas ref={this.canvasRef} width={this.gridWidth} height={this.gridWidth} />
            </Waypoint>
            {percentAliveSlider}
          </div>
          {playbackControls}
          <div style={{height: "0.5em"}}/>
          {highlightedSlider}

          {transmissionProbabilitySlider}
          {immunityFractionSlider}
          {vacuumProbSlider}
          {degreeSlider}

          {maxActiveNodesSlider}
          {toggleLongDistanceNetwork}
          {networkEffectSlider}

          {realScientistFractionSlider}
          {scientistPreferenceForTruthSlider}
          {careeristPreferenceForTruthSlider}

          {protip}


          {/*{speedSlider}*/}
          <Interval milliseconds={intervalMillis} callback={this.onTick} />
        </div>
    )
  }
}